<script lang="ts" setup>
import { onBeforeMount, ref } from "vue";
import {
  HousingApplication,
  HousingApplicationStatus,
  HousingFinderInput,
  HousingSiteDto,
  HousingSiteResult,
  HousingSiteResultType,
} from "~/api";
import { apiClient } from "~/utils/apiClient";
import { showError } from "~/utils/handleAPIError";
import Finder from "../../Intake/pages/Finder.vue";

const props = defineProps<{
  applicationGuid: string;
  isReadOnly?: boolean;
}>();

const emit = defineEmits<{
  (e: "updateSelected", sitesQualifiedFor: HousingSiteResult[]): void;
}>();

/** Used to rerender the finder when the inputs are fetched. */
const inputsKey = ref<number>(0);
/** The application associated with the given guid. */
const housingApplication = ref<HousingApplication>();
/** The inputs associated with the application. */
const inputs = ref<HousingFinderInput>();
/** A reference to the Finder child component instance. This allows us to call any methods exposed in Finder.vue*/
const finder = ref();
/** The guid of the application. */
const applicationGuid = ref<string>(props.applicationGuid as string);
const previousFinderInputString = ref<string>();

const previousSitesString = ref<string>();

const sitesSelected = ref<string[]>([]);
const housingSites = ref<HousingSiteDto[]>([]);

/**
 * Saves the finder info to the database
 */
const createOverrideFinderInfo = async (
  overrideFinderInput: HousingFinderInput,
  originalFinderInput: HousingFinderInput,
) => {
  if (!applicationGuid.value) {
    showError("Application guid not found");
    return;
  }

  overrideFinderInput.isOverrideOf = originalFinderInput.guid;
  overrideFinderInput.housingApplicationGuid = undefined;

  await apiClient.finderInput
    .createFinderInput(overrideFinderInput)
    .then((finderInput) => {
      inputs.value = finderInput;
      previousFinderInputString.value = stringifyFinderInput(finderInput);
      // housingApplicationDto.value?.finderInputOverride = finderInput;
    })
    .catch(showError);
};

/**
 * Auto-save handler for filter changes (watcher in Finder.vue).
 * - Draft apps: updates the original record in-place (HMB is setting up the app).
 * - Post-Draft apps with an override: updates the override in-place.
 * - Post-Draft apps without an override: skips save — the original must not be
 *   modified. An override will be created when the user clicks Calculate.
 */
const handleAutoSaveFinderInfo = async (finderInput: HousingFinderInput) => {
  const newFinderInputsString = stringifyFinderInput(finderInput);
  if (newFinderInputsString === previousFinderInputString.value) {
    return;
  }

  if (!inputs.value?.guid) {
    return;
  }

  if (
    !inputs.value.isOverrideOf &&
    housingApplication.value?.status !== HousingApplicationStatus.DRAFT
  ) {
    return;
  }

  await apiClient.finderInput
    .updateFinderInput({ ...finderInput, guid: inputs.value.guid })
    .then((fi) => {
      inputs.value = fi;
      previousFinderInputString.value = stringifyFinderInput(fi);
    })
    .catch(showError);
};

/**
 * Ensures an override FinderInput exists before a recalculation runs.
 * For post-Draft apps with no override yet, creates one to preserve the original.
 * For Draft apps, edits the original directly (HMB is setting up the app).
 */
const ensureOverrideExists = async () => {
  if (
    inputs.value &&
    !inputs.value.isOverrideOf &&
    housingApplication.value?.status !== HousingApplicationStatus.DRAFT
  ) {
    await createOverrideFinderInfo({ ...inputs.value }, inputs.value);
  }
};

/**
 * Handles the recalculation event from Finder.vue (user clicked Calculate).
 * This is the deliberate action that creates an override if needed,
 * then saves the updated site results.
 */
const handleRecalculation = async (sites: HousingSiteResult[]) => {
  await ensureOverrideExists();
  await updateSiteResultsInApplication(sites, true);
};

/**
 * Updates the site results in the application
 */
const updateSiteResultsInApplication = async (
  sitesQualifiedFor: HousingSiteResult[],
  isRecalculation = false,
) => {
  if (!housingApplication.value) {
    showError("Housing application not found");
    return;
  }

  if (!isRecalculation) {
    // Explicit selection: mark sites as Preferred/Qualified based on user picks
    sitesQualifiedFor.forEach((site) => {
      if (site.resultType === HousingSiteResultType.NO_OFFER) {
        return;
      } else if (site.siteCode && sitesSelected.value.includes(site.siteCode)) {
        site.resultType = HousingSiteResultType.PREFERRED;
      } else {
        site.resultType = HousingSiteResultType.QUALIFIED;
      }
    });
  }

  // only save if changed
  const newSitesString = stringifySitesQualifiedFor(sitesQualifiedFor);
  if (newSitesString === previousSitesString.value) {
    return;
  }

  await apiClient.housingApplication
    .updateSiteResults(
      housingApplication.value.guid,
      isRecalculation,
      sitesQualifiedFor,
    )
    .then((dto) => {
      housingApplication.value = dto;
      previousSitesString.value = stringifySitesQualifiedFor(dto.siteResults);
      if (dto.siteResults) {
        emit("updateSelected", dto.siteResults);
      }
      computePreferences();
    })
    .catch(showError);
};

const stringifySitesQualifiedFor = (
  sitesQualifiedFor: HousingSiteResult[] | null | undefined,
) => {
  if (!sitesQualifiedFor) {
    return "";
  }
  return JSON.stringify(sitesQualifiedFor);
};

const stringifyFinderInput = (finderInput: HousingFinderInput) => {
  return JSON.stringify(finderInput, (key, value) => {
    if (key === "searchDate") {
      return undefined;
    }
    return value;
  });
};

/**
 * Gets the finder inputs associated with the given application guid
 */
const getUsersFinderInputs = async () => {
  await apiClient.finderInput
    .getFinderInputForHousingApplication(props.applicationGuid)
    .then((finderInput) => {
      inputs.value = finderInput;
      previousFinderInputString.value = stringifyFinderInput(finderInput);
    })
    .catch((error) => {
      if (error.message.includes("Not Found")) {
        return;
      } else {
        showError(error);
      }
    });
};

/**
 * This method iterates through the site results and updates the site preferences
 */
const computePreferences = () => {
  sitesSelected.value = housingApplication.value?.siteResults
    ?.filter((site) => site.resultType === HousingSiteResultType.PREFERRED)
    .map((site) => site.siteCode) as string[];
};

/**
 * Calls updateSiteResultsInApplication with the current siteResults
 * @param selected - An array of selected site codes
 */
const handleConfirmSelection = async () => {
  if (housingApplication.value?.siteResults) {
    await updateSiteResultsInApplication(housingApplication.value?.siteResults);
  }
};

/**
 * Save the initial finder inputs. Only used when it is an offline application, otherwise they should already have inputs.
 */
const handleSaveFinderInfo = async (finderInput: HousingFinderInput) => {
  if (!applicationGuid.value) {
    showError("Application guid not found");
    return;
  }

  finderInput.housingApplicationGuid = applicationGuid.value;
  await apiClient.finderInput
    .createFinderInput(finderInput)
    .then((finderInput) => {
      inputs.value = finderInput;
      previousFinderInputString.value = stringifyFinderInput(finderInput);
    })
    .catch(showError);
};

const refreshHousingApplication = async () => {
  housingApplication.value =
    await apiClient.housingApplication.getHousingApplication(
      props.applicationGuid,
    );
};

onBeforeMount(async () => {
  await Promise.all([getUsersFinderInputs(), refreshHousingApplication()]);
  computePreferences();

  previousSitesString.value = stringifySitesQualifiedFor(
    housingApplication.value?.siteResults,
  );

  inputsKey.value++;
});
</script>

<template>
  <div class="ApplicationFinderLayout">
    <Finder
      :key="inputsKey"
      ref="finder"
      :inputs="inputs"
      :application="housingApplication"
      :favourite-sites="sitesSelected"
      :is-read-only="isReadOnly"
      initial-collapse-locations
      initial-show-only-selected
      @update-sites-qualified-for="handleRecalculation"
      @update-selection="(sites: string[]) => (sitesSelected = sites)"
      @set-sites="(sites: HousingSiteDto[]) => (housingSites = sites)"
      @update-finder-info="handleAutoSaveFinderInfo"
      @save-finder-info="handleSaveFinderInfo"
      @confirm-selection="handleConfirmSelection"
    />
  </div>
</template>

<style lang="less">
.ApplicationFinderLayout {
  height: 100%;

  &:has(.calculator-box) {
    display: flex;
    justify-content: center;
  }

  .FinderPage {
    height: 100%;

    .background {
      top: 70px;
      background-color: white;
      height: 120px;
    }
    .results {
      height: 100%;

      .results-header {
        background-color: var(--el-color-info-light-9);
        padding-top: 30px;
        padding-bottom: 47px;
        //top: -20px;
        position: sticky;
        top: 0;
        margin: 0 0px;
        border: 1px solid var(--el-color-info-light-7);
        border-bottom: 4px solid var(--el-color-primary);
        border-radius: 4px;
      }
      .resultList {
        display: flex;
        flex-direction: column;
        margin-top: 10px;
      }
      .filter-box {
        display: flex;
        background-color: var(--el-color-info-light-9);
        border: 1px solid rgba(99, 99, 99, 0.2);

        position: sticky;
        top: 0;
        max-height: 800px;
        height: 80vh;
      }
    }
  }
}
</style>
